/**
 * Notifications Context
 * Client-side notification feed derived from backend asset/event data.
 * No backend notification service — transforms "recent events" (assets with lastEvent, timeOfLogLong)
 * into a filterable notification stream with event-driven alert logic (e.g. zone departures, time windows).
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";

const NOTIFICATIONS_LAST_SEEN_KEY = "atlas_notifications_last_seen";
const NOTIFICATIONS_CACHE_KEY = "atlas_notifications_cache";

const DEFAULT_TIME_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** Event types we treat as "notifications" (alert-worthy). Backend only gives us data; we classify here. */
export const NOTIFICATION_EVENT_TYPES = {
  ZONE_DEPARTURE: "Zone Departure",
  ZONE_ARRIVAL: "Zone Arrival",
  LEAVE_GEOFENCE: "Leave Geofence",
  ENTER_GEOFENCE: "Enter Geofence",
  ORIGIN: "Origin",
  RECEIVE: "Receive",
  MOVING: "Moving",
  ALERT: "Alert",
  WARNING: "Warning",
  SCAN: "Scan",
  PROXIMITY: "Proximity",
  DEFAULT: "Event",
};

/** Time window options for filtering */
export const TIME_WINDOWS = {
  "24h": { label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  "7d": { label: "Last 7 days", ms: SEVEN_DAYS_MS },
  all: { label: "All time", ms: null },
};

/**
 * Build notification list from assets (backend data).
 * Filters by event type and time window; optional user filter if assets carry appUserId.
 */
export function buildNotificationsFromAssets({
  assets = [],
  facilities = {},
  eventTypeFilter = [], // e.g. ["Zone Departure", "Zone Arrival"] or empty = all
  timeWindowKey = "24h",
  userFilterId = null, // if set, only include events for this appUserId (when asset has it)
}) {
  const now = Date.now();
  const windowMs = TIME_WINDOWS[timeWindowKey]?.ms ?? DEFAULT_TIME_WINDOW_MS;
  const cutoff = windowMs ? now - windowMs : 0;

  const notifications = [];
  (assets || []).forEach((asset) => {
    if (!asset.timeOfLogLong || (windowMs && asset.timeOfLogLong < cutoff)) return;
    if (userFilterId && asset.appUserId && String(asset.appUserId) !== String(userFilterId)) return;

    const eventName = asset.lastEvent || NOTIFICATION_EVENT_TYPES.DEFAULT;
    if (
      eventTypeFilter.length > 0 &&
      !eventTypeFilter.some((f) => (eventName || "").toLowerCase().includes((f || "").toLowerCase()))
    ) {
      return;
    }

    const facilityName =
      asset.facilityId && facilities[asset.facilityId]
        ? facilities[asset.facilityId].name
        : asset.facilityName || null;

    notifications.push({
      id: `n-${asset.assetId || asset.id}-${asset.timeOfLogLong}`,
      assetId: asset.assetId || asset.id,
      tag: asset.tag || asset.assetTag || "Unknown",
      event: eventName,
      timestamp: asset.timeOfLogLong,
      facility: facilityName,
      facilityId: asset.facilityId,
      hasLocation: !!(asset.latitude && asset.longitude),
      severity: getEventSeverity(eventName),
    });
  });

  return notifications.sort((a, b) => b.timestamp - a.timestamp);
}

function getEventSeverity(eventName) {
  const e = (eventName || "").toLowerCase();
  if (e.includes("departure") || e.includes("leave") || e.includes("alert") || e.includes("warning"))
    return "warning";
  if (e.includes("arrival") || e.includes("enter") || e.includes("receive") || e.includes("origin"))
    return "info";
  return "default";
}

function loadLastSeen() {
  try {
    const v = localStorage.getItem(NOTIFICATIONS_LAST_SEEN_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

function saveLastSeen(ts) {
  try {
    localStorage.setItem(NOTIFICATIONS_LAST_SEEN_KEY, String(ts));
  } catch {}
}

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const [lastSeenAt, setLastSeenAt] = useState(loadLastSeen);
  const [source, setSource] = useState({ assets: [], facilities: {} });
  const [eventTypeFilter, setEventTypeFilter] = useState([]);
  const [timeWindowKey, setTimeWindowKey] = useState("24h");
  const [userFilterId, setUserFilterId] = useState(null);

  const notifications = useMemo(() => {
    return buildNotificationsFromAssets({
      assets: source.assets,
      facilities: source.facilities,
      eventTypeFilter,
      timeWindowKey,
      userFilterId,
    });
  }, [source.assets, source.facilities, eventTypeFilter, timeWindowKey, userFilterId]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => n.timestamp > lastSeenAt).length;
  }, [notifications, lastSeenAt]);

  const markAsSeen = useCallback(() => {
    const now = Date.now();
    setLastSeenAt(now);
    saveLastSeen(now);
  }, []);

  const setNotificationSource = useCallback(({ assets = [], facilities = {} } = {}) => {
    setSource({ assets, facilities: facilities || {} });
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      lastSeenAt,
      markAsSeen,
      setNotificationSource,
      eventTypeFilter,
      setEventTypeFilter,
      timeWindowKey,
      setTimeWindowKey,
      userFilterId,
      setUserFilterId,
    }),
    [
      notifications,
      unreadCount,
      lastSeenAt,
      markAsSeen,
      setNotificationSource,
      eventTypeFilter,
      timeWindowKey,
      userFilterId,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}

export function useNotificationsOptional() {
  return useContext(NotificationsContext);
}
